import { useEffect, useState, useRef } from "react";
import { ClipboardList, Plus, FileCheck, X, AlertTriangle } from "lucide-react";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import toast from "react-hot-toast";

import { EnrollmentTable } from "../../components/enrollments/enrollments.component";
import PageWrapper from "../../components/page-wrapper/page-wrapper.components";
import { selectCurrentUser } from "../../redux/user/user.selectors";
import { sessionExpired, updateToken } from "../../redux/user/user.actions";
import { authFetch, getApiUrl, SessionExpiredError } from "../../utils/api";

const EnrollmentsDashboardPage = ({ currentUser, onSessionExpired, onTokenRefresh }) => {
    const [enrollments, setEnrollments] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [showRegularEnrollmentModal, setShowRegularEnrollmentModal] = useState(false);
    const [loadingError, setLoadingError] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState("hsa");
    const toastIdRef = useRef(null);

    const [enrollInfo, setEnrollInfo] = useState({
        clientFirstName: "",
        clientLastName: "",
        clientEmail: "",
    });

    const [policyName, setPolicyName] = useState("");
    const [adminFee, setAdminFee] = useState(10);
    const [enrollmentFee, setEnrollmentFee] = useState("");
    const [requiresBackdatingAck, setRequiresBackdatingAck] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [enrollmentToDelete, setEnrollmentToDelete] = useState(null);
    const [filterStatus, setFilterStatus] = useState("Show all");
    const hasFetchedRef = useRef(false);

    const apiUrl = getApiUrl();
    const authParams = { currentUser, onSessionExpired, onTokenRefresh };

    const fetchEnrollments = async (showToast = true) => {
        if (showToast && toastIdRef.current) {
            toast.dismiss(toastIdRef.current);
        }
        if (showToast) {
            toastIdRef.current = toast.loading('Loading enrollments...');
        }
        try {
            const response = await authFetch(
                `${apiUrl}/enrollment/${currentUser.id}`,
                { method: 'GET' },
                authParams
            );
            if (response.ok) {
                const data = await response.json();
                setEnrollments(data.enrollments);
                if (showToast && toastIdRef.current) {
                    toast.dismiss(toastIdRef.current);
                }
            } else {
                if (showToast && toastIdRef.current) {
                    toast.error('Failed to load enrollments', { id: toastIdRef.current });
                }
            }
        } catch (err) {
            if (!(err instanceof SessionExpiredError)) {
                setLoadingError(true);
            }
            if (showToast && toastIdRef.current) {
                toast.dismiss(toastIdRef.current);
            }
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (hasFetchedRef.current) return;
        hasFetchedRef.current = true;
        fetchEnrollments();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleFilterChange = (e) => {
        setFilterStatus(e.target.value);
    };

    const filteredEnrollments = enrollments.filter((item) => {
        if (filterStatus === "Show all") return true;
        return item.status.toLowerCase() === filterStatus.split(" ")[1];
    });

    const deleteEnrollment = async (id) => {
        try {
            const response = await authFetch(
                `${apiUrl}/enrollment/${id}`,
                {
                    method: 'DELETE',
                    headers: { 'Content-Type': 'application/json' },
                },
                authParams
            );

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            fetchEnrollments();
        } catch (error) {
            if (!(error instanceof SessionExpiredError)) {
                console.error('Error:', error);
            }
        }
    };

    const createEnrollment = () => {
        setShowRegularEnrollmentModal(true);
    };

    const handleClose = () => {
        setShowRegularEnrollmentModal(false);
        resetForm();
    };

    const resetForm = () => {
        setSelectedPlan("hsa");
        setEnrollInfo({
            clientFirstName: "",
            clientLastName: "",
            clientEmail: "",
        });
        setPolicyName("");
        setEnrollmentFee("");
        setRequiresBackdatingAck(false);
    };

    const handleEnrollInfoChange = (e) => {
        const { id, value } = e.target;
        setEnrollInfo((prev) => ({
            ...prev,
            [id]: value,
        }));
    };

    const handleSubmit = async (sendEmail) => {
        const allData = {
            enrollInfo,
            selectedPlan,
            enrollmentFee,
            requiresBackdatingAck
        };

        const submitToastId = toast.loading('Creating enrollment...');
        try {
            const response = await authFetch(
                `${apiUrl}/enrollment/create`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        id: currentUser.id,
                        policyName,
                        selectedPlan,
                        adminFee,
                        enrollmentFee,
                        enrollmentData: JSON.stringify(allData),
                        sendEmail: sendEmail,
                    }),
                },
                authParams
            );
            if (response.ok) {
                const data = await response.json();
                if (!sendEmail) {
                    let enrollmentId = data.enrollmentId;
                    window.open(`/enroll/${enrollmentId}?mode=edit`, "_blank");
                }
                handleClose();
                toast.success('Enrollment created', { id: submitToastId });
                fetchEnrollments(false);
            } else {
                toast.error('Failed to create enrollment', { id: submitToastId });
            }
        } catch (err) {
            if (!(err instanceof SessionExpiredError)) {
                setLoadingError(true);
            }
            toast.dismiss(submitToastId);
        }
    };

    const handleDeleteClick = (id) => {
        setEnrollmentToDelete(id);
        setShowDeleteModal(true);
    };

    const handleConfirmDelete = () => {
        deleteEnrollment(enrollmentToDelete);
        setShowDeleteModal(false);
    };

    const planOptions = [
        { value: "hsa", label: "Health Spending Account" },
        { value: "lsa", label: "Lifestyle Spending Account" },
        { value: "fixed", label: "HSA + LSA (Fixed)" },
        { value: "flex", label: "Flexible Spending Account" },
    ];

    const isFormValid = enrollInfo.clientFirstName && enrollInfo.clientLastName && enrollInfo.clientEmail && policyName && adminFee > 0 && enrollmentFee;

    return (
        <PageWrapper>
            <div className="flex-1 px-6 md:px-12 lg:pl-12 lg:pr-24 py-6 pb-5 overflow-y-auto">
                {/* page header with icon */}
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-primary/10 rounded-xl">
                            <ClipboardList className="w-6 h-6 text-primary" />
                        </div>
                        <div>
                            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
                                Policy Enrollments
                            </h1>
                            <p className="text-sm text-gray-500">
                                Enrollments you’ve created{!isLoading && ` (${enrollments.length} total)`}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={createEnrollment}
                        className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-xl text-sm font-medium hover:bg-primary-dark active:scale-[0.98] transition-all"
                    >
                        <Plus className="w-4 h-4" />
                        <span className="hidden sm:inline">Create</span>
                    </button>
                </div>

                {/* content */}
                <div>
                    {isLoading ? null : enrollments.length === 0 ? (
                        <div className="bg-white rounded-2xl shadow-card p-6">
                            <p className="text-center text-gray-500">
                                There are currently no enrollments pending.{" "}
                                <button onClick={createEnrollment} className="text-primary hover:text-primary-dark font-medium">
                                    Click here
                                </button>{" "}
                                to begin the enrollment process.
                            </p>
                        </div>
                    ) : (
                        <div className="bg-white rounded-2xl shadow-card p-6">
                            <div className="flex items-center gap-3 mb-4">
                                <label htmlFor="filter-select" className="text-sm font-medium text-gray-700">Filter:</label>
                                <select
                                    id="filter-select"
                                    className="px-4 py-2 border border-gray-200 rounded-xl text-sm text-gray-700 bg-white focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all"
                                    value={filterStatus}
                                    onChange={handleFilterChange}
                                >
                                    <option value="Show all">Show all</option>
                                    <option value="Show pending">Show unsigned</option>
                                    <option value="Show signed">Show signed</option>
                                    <option value="Show complete">Show enrolled</option>
                                </select>
                            </div>
                            <div className="overflow-x-auto">
                                <EnrollmentTable
                                    fetchEnrollments={() => fetchEnrollments(false)}
                                    currentItems={filteredEnrollments}
                                    handleDeleteClick={handleDeleteClick}
                                    currentUser={currentUser}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* create enrollment modal */}
            {showRegularEnrollmentModal && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-modal w-full max-w-2xl max-h-[90vh] flex flex-col">
                        <div className="flex items-center justify-between p-4 border-b border-gray-100 shrink-0">
                            <h3 className="text-lg font-semibold text-gray-900">Generate Enrollment</h3>
                            <button
                                onClick={handleClose}
                                className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="p-4 space-y-6 flex-1 overflow-y-auto">
                            <p className="text-sm text-gray-600">
                                Enter your client's company name, plan, admin fee, and client details to generate an enrollment form.
                                You can fill out as much information as you have available now, and your client can complete the rest later.
                            </p>

                            {/* company name */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Company Name</label>
                                <input
                                    type="text"
                                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-gray-900 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all"
                                    value={policyName}
                                    onChange={(e) => setPolicyName(e.target.value)}
                                    required
                                />
                            </div>

                            {/* fees row */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Total Admin Fee</label>
                                    <select
                                        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-gray-900 bg-white focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all"
                                        value={adminFee}
                                        onChange={(e) => setAdminFee(e.target.value)}
                                    >
                                        {[5, 5.5, 6, 6.5, 7, 7.5, 8, 8.5, 9, 9.5, 10].map((val) => (
                                            <option key={val} value={val}>{val}%</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Enrollment Fee</label>
                                    <div className="relative">
                                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                                        <input
                                            type="text"
                                            className="w-full pl-8 pr-4 py-2.5 border border-gray-200 rounded-xl text-gray-900 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all"
                                            value={enrollmentFee}
                                            onChange={(e) => setEnrollmentFee(e.target.value)}
                                            onKeyDown={(e) => {
                                                const char = e.key;
                                                if (char !== "." && char !== "Backspace" && char !== "Tab" && (char < "0" || char > "9")) {
                                                    e.preventDefault();
                                                }
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* client info row */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">First Name</label>
                                    <input
                                        type="text"
                                        id="clientFirstName"
                                        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-gray-900 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all"
                                        value={enrollInfo.clientFirstName}
                                        onChange={handleEnrollInfoChange}
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Last Name</label>
                                    <input
                                        type="text"
                                        id="clientLastName"
                                        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-gray-900 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all"
                                        value={enrollInfo.clientLastName}
                                        onChange={handleEnrollInfoChange}
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                                    <input
                                        type="email"
                                        id="clientEmail"
                                        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-gray-900 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all"
                                        value={enrollInfo.clientEmail}
                                        onChange={handleEnrollInfoChange}
                                        required
                                    />
                                </div>
                            </div>

                            {/* backdating acknowledgement toggle */}
                            <div>
                                <label className="flex items-start gap-3 cursor-pointer p-3 bg-gray-50 rounded-xl border border-gray-200">
                                    <input
                                        type="checkbox"
                                        checked={requiresBackdatingAck}
                                        onChange={(e) => setRequiresBackdatingAck(e.target.checked)}
                                        className="mt-0.5 w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                                    />
                                    <span className="text-sm text-gray-700">
                                        Will this client require a Backdating Acknowledgement form? <span className="text-gray-500">(checking surfaces form to client)</span>
                                    </span>
                                </label>
                            </div>

                            {/* select plan */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-3">Select Plan</label>
                                <div className="grid grid-cols-2 gap-3">
                                    {planOptions.map((plan) => (
                                        <button
                                            key={plan.value}
                                            type="button"
                                            onClick={() => setSelectedPlan(plan.value)}
                                            className={`p-4 rounded-xl border-2 text-center transition-all ${
                                                selectedPlan === plan.value
                                                    ? "border-primary bg-primary/5 text-primary"
                                                    : "border-gray-200 hover:border-gray-300 text-gray-700"
                                            }`}
                                        >
                                            <span className="font-medium text-sm">{plan.label}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* note */}
                            <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl text-sm text-gray-600">
                                <FileCheck className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                                <p>
                                    After the enrollment form is completed, National HealthClaim (NHC) will be notified and will begin the review process.
                                    If there are any questions, NHC may reach out to the company's Plan Administrator or Advisor for further discussion.
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-100 shrink-0">
                            <button
                                onClick={handleClose}
                                className="h-9 px-4 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={(e) => {
                                    e.preventDefault();
                                    handleSubmit(false);
                                }}
                                disabled={!isFormValid}
                                className="inline-flex items-center justify-center h-9 px-4 text-sm font-medium text-white bg-primary rounded-xl hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Submit
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* delete confirmation modal */}
            {showDeleteModal && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-modal w-full max-w-sm p-5">
                        <div className="flex items-start gap-3 mb-4">
                            <div className="p-2 bg-red-50 rounded-lg flex-shrink-0">
                                <AlertTriangle className="w-5 h-5 text-red-500" />
                            </div>
                            <div>
                                <h3 className="font-semibold text-gray-900">Delete Enrollment</h3>
                                <p className="text-sm text-gray-500 mt-1">This action cannot be undone.</p>
                            </div>
                        </div>
                        <div className="flex justify-end gap-2">
                            <button
                                onClick={() => setShowDeleteModal(false)}
                                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleConfirmDelete}
                                className="px-4 py-2 text-sm font-medium text-white bg-red-500 hover:bg-red-600 rounded-lg transition-colors"
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </PageWrapper>
    );
};

const mapStateToProps = createStructuredSelector({
    currentUser: selectCurrentUser,
});

const mapDispatchToProps = (dispatch) => ({
    onSessionExpired: () => dispatch(sessionExpired()),
    onTokenRefresh: (token) => dispatch(updateToken(token)),
});

export default connect(mapStateToProps, mapDispatchToProps)(EnrollmentsDashboardPage);
